import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FiSearch, FiX, FiChevronDown, FiUser, FiCheck } from 'react-icons/fi';

export interface SelectOption {
    value: string;
    label: string;
    sub?: string; // secondary text (zone, etc.)
}

interface BaseProps {
    options: SelectOption[];
    placeholder?: string;
    icon?: React.ReactNode;
    disabled?: boolean;
}

interface SingleProps extends BaseProps {
    multiple?: false;
    value: string;
    onChange: (val: string) => void;
}

interface MultiProps extends BaseProps {
    multiple: true;
    value: string[];
    onChange: (val: string[]) => void;
}

type SearchableSelectProps = SingleProps | MultiProps;

const SearchableSelect: React.FC<SearchableSelectProps> = (props) => {
    const { options, placeholder = '-- Selectionner --', icon, disabled } = props;
    const multiple = props.multiple === true;

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const filtered = useMemo(() => {
        if (!search) return options;
        const lower = search.toLowerCase();
        return options.filter(o => o.label.toLowerCase().includes(lower) || (o.sub && o.sub.toLowerCase().includes(lower)));
    }, [options, search]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
    }, [open]);

    // Helpers
    const isSelected = (val: string) => {
        if (multiple) return (props as MultiProps).value.includes(val);
        return (props as SingleProps).value === val;
    };

    const toggleOption = (val: string) => {
        if (multiple) {
            const mp = props as MultiProps;
            if (mp.value.includes(val)) {
                mp.onChange(mp.value.filter(v => v !== val));
            } else {
                mp.onChange([...mp.value, val]);
            }
        } else {
            (props as SingleProps).onChange(val);
            setOpen(false);
            setSearch('');
        }
    };

    const clearAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (multiple) (props as MultiProps).onChange([]);
        else (props as SingleProps).onChange('');
        setSearch('');
    };

    const selectAll = () => {
        if (multiple) (props as MultiProps).onChange(options.map(o => o.value));
    };

    // Display label
    let displayLabel = '';
    let hasValue = false;
    if (multiple) {
        const vals = (props as MultiProps).value;
        hasValue = vals.length > 0;
        if (vals.length === 0) displayLabel = '';
        else if (vals.length === 1) displayLabel = options.find(o => o.value === vals[0])?.label || '1 selectionne';
        else displayLabel = `${vals.length} selectionnes`;
    } else {
        const val = (props as SingleProps).value;
        hasValue = !!val;
        displayLabel = options.find(o => o.value === val)?.label || '';
    }

    const initials = (label: string) => label.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

    return (
        <div ref={ref} style={{ position: 'relative', minWidth: 180, width: '100%' }}>
            <div
                onClick={() => !disabled && setOpen(p => !p)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd',
                    background: disabled ? '#f9fafb' : '#fff', cursor: disabled ? 'default' : 'pointer', fontSize: 13,
                    minHeight: 36
                }}
            >
                {icon || <FiUser size={13} style={{ color: '#999', flexShrink: 0 }} />}
                {multiple && hasValue ? (
                    <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4, overflow: 'hidden' }}>
                        {(props as MultiProps).value.slice(0, 3).map(v => {
                            const opt = options.find(o => o.value === v);
                            return (
                                <span key={v} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    background: '#EFF6FF', color: '#3B82F6', padding: '1px 6px',
                                    borderRadius: 4, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap'
                                }}>
                                    {opt ? opt.label.split(' ').slice(0, 2).join(' ') : v}
                                    <FiX size={11} style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); toggleOption(v); }} />
                                </span>
                            );
                        })}
                        {(props as MultiProps).value.length > 3 && (
                            <span style={{ fontSize: 11, color: '#666', alignSelf: 'center' }}>+{(props as MultiProps).value.length - 3}</span>
                        )}
                    </div>
                ) : (
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: hasValue ? '#111' : '#999' }}>
                        {hasValue ? displayLabel : placeholder}
                    </span>
                )}
                {hasValue && (
                    <span onClick={clearAll} style={{ cursor: 'pointer', color: '#999', display: 'flex' }}>
                        <FiX size={13} />
                    </span>
                )}
                <FiChevronDown size={13} style={{ color: '#999', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </div>
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4,
                    maxHeight: 320, display: 'flex', flexDirection: 'column'
                }}>
                    <div style={{ padding: '8px 8px 4px', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: '#f9fafb', borderRadius: 6 }}>
                            <FiSearch size={13} style={{ color: '#999' }} />
                            <input
                                ref={inputRef}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Rechercher..."
                                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, width: '100%', padding: '2px 0' }}
                            />
                            {search && <FiX size={12} style={{ cursor: 'pointer', color: '#999' }} onClick={() => setSearch('')} />}
                        </div>
                        {multiple && (
                            <div style={{ display: 'flex', gap: 8, padding: '6px 4px 2px', fontSize: 11 }}>
                                <span style={{ color: '#3B82F6', cursor: 'pointer', fontWeight: 500 }} onClick={selectAll}>Tout selectionner</span>
                                <span style={{ color: '#999' }}>|</span>
                                <span style={{ color: '#EF4444', cursor: 'pointer', fontWeight: 500 }} onClick={() => (props as MultiProps).onChange([])}>Tout deselectionner</span>
                                <span style={{ marginLeft: 'auto', color: '#666' }}>{(props as MultiProps).value.length}/{options.length}</span>
                            </div>
                        )}
                    </div>

                    <div style={{ overflowY: 'auto', maxHeight: 240 }}>
                        {!multiple && (
                            <div
                                onClick={() => { toggleOption(''); setOpen(false); setSearch(''); }}
                                style={{
                                    padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: '#999',
                                    background: !hasValue ? '#f0fdf4' : 'transparent'
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
                                onMouseLeave={e => (e.currentTarget.style.background = !hasValue ? '#f0fdf4' : 'transparent')}
                            >
                                {placeholder}
                            </div>
                        )}
                        {filtered.length === 0 ? (
                            <div style={{ padding: 12, fontSize: 12, color: '#999', textAlign: 'center' }}>Aucun resultat</div>
                        ) : (
                            filtered.map(opt => {
                                const sel = isSelected(opt.value);
                                return (
                                    <div
                                        key={opt.value}
                                        onClick={() => toggleOption(opt.value)}
                                        style={{
                                            padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                                            background: sel ? '#f0fdf4' : 'transparent',
                                            fontWeight: sel ? 600 : 400,
                                            color: sel ? '#16A34A' : '#333',
                                            display: 'flex', alignItems: 'center', gap: 8
                                        }}
                                        onMouseEnter={e => (e.currentTarget.style.background = sel ? '#dcfce7' : '#f3f4f6')}
                                        onMouseLeave={e => (e.currentTarget.style.background = sel ? '#f0fdf4' : 'transparent')}
                                    >
                                        {multiple && (
                                            <span style={{
                                                width: 18, height: 18, borderRadius: 4,
                                                border: sel ? '2px solid #16A34A' : '2px solid #d1d5db',
                                                background: sel ? '#16A34A' : '#fff',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                            }}>
                                                {sel && <FiCheck size={12} color="#fff" />}
                                            </span>
                                        )}
                                        <span style={{
                                            width: 26, height: 26, borderRadius: '50%',
                                            background: sel ? '#dcfce7' : '#e5e7eb',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 10, fontWeight: 700, color: sel ? '#16A34A' : '#666', flexShrink: 0
                                        }}>
                                            {initials(opt.label)}
                                        </span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</div>
                                            {opt.sub && <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{opt.sub}</div>}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
